'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries, fetchLanguages } from '@eliadmin/core/src/api/general/general-api';
import { fetchEmploymentTypes } from '@eliadmin/core/src/api/jobportal/create-curriculum-api';
import { fetchJobCategories } from '@eliadmin/core/src/api/jobportal/categories-api';
// import { MultiSelect } from '@eliadmin/core/src/components/ui/multi-select';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useJobportalOffersStore } from '@eliadmin/core/src/stores/jobportal-offers-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { toCategoryTreeCheckboxItems } from '@eliadmin/core/src/utils/jobportal-category-utils';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { RangeFilter } from '@eliadmin/core/src/components/jobportal/filter/fields/jobportal-filter-range';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { CheckboxesFilter } from '@eliadmin/core/src/components/jobportal/filter/fields/jobportal-filter-checkboxes';
import type { JobportalOffersFilter as JobportalOffersFilterType } from '@eliadmin/core/src/stores/jobportal-offers-store';

type JobPortalOffersFilterProps = {
  value?: JobportalOffersFilterType;
  onChange?: (patch: Partial<JobportalOffersFilterType>) => void;
  showClearButton?: boolean;
};

export function JobPortalOffersFilter(props: JobPortalOffersFilterProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const {
    filter: storeFilter,
    setFilter: storeSetFilter,
    salaryRanges,
  } = useJobportalOffersStore();
  const clearFilters = useJobportalOffersStore(s => s.clearFilters);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const filter = props.value ?? storeFilter;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setFilter = (patch: Partial<JobportalOffersFilterType>) => {
    if (props.onChange) {
      props.onChange(patch);
      return;
    }
    storeSetFilter(patch);
  };

  const [categoriesSearch, setCategoriesSearch] = useState('');
  const [debouncedCategoriesSearch, setDebouncedCategoriesSearch] = useState('');
  const [openedCategoryAccordions, setOpenedCategoryAccordions] = useState<string[]>([]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedCategoriesSearch(categoriesSearch);
    }, 300);
    return () => clearTimeout(handle);
  }, [categoriesSearch]);

  const categoriesQuery = useInfiniteQuery({
    queryKey: [CacheKey.JOBPORTAL_CATEGORIES, CacheKey.INFINITE, locale, debouncedCategoriesSearch],
    queryFn: ({ pageParam }) =>
      fetchJobCategories({
        locale,
        query: {
          results: 50,
          childs: true,
          where: { parent: { exists: false } },
          page: pageParam,
          ...(debouncedCategoriesSearch.trim() ? { search: debouncedCategoriesSearch.trim() } : {}),
        },
      }),
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page) {
        return lastPage?.meta?.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    placeholderData: prev => prev,
  });

  const categoriesData = useMemo(
    () => categoriesQuery.data?.pages.flatMap(page => page.data) ?? [],
    [categoriesQuery.data],
  );
  const categories = useMemo(() => toCategoryTreeCheckboxItems(categoriesData), [categoriesData]);
  const isLoadingCategories = categoriesQuery.isLoading;
  const showCategoriesSkeleton = isLoadingCategories && !categories.length;

  const { data: employmentTypes, isLoading: isLoadingEmploymentTypes } = useQuery({
    queryKey: [CacheKey.JOBPORTAL_EMPLOYMENT_TYPES, locale],
    queryFn: () =>
      fetchEmploymentTypes({ locale }).then(r =>
        r.data.map(g => ({ label: g.name, value: g.id.toString() })),
      ),
  });

  const { data: languages, isLoading: isLoadingLanguages } = useQuery({
    queryKey: [CacheKey.LANGUAGES],
    queryFn: () =>
      fetchLanguages().then(r => r.data.map(g => ({ label: g.name, value: g.id.toString() }))),
  });

  const { data: countriesResponse = { data: [] }, isLoading: isLoadingCountries } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
  });
  const countries = countriesResponse.data ?? [];

  const [salaryRange, setSalaryRange] = useState<string[]>([
    salaryRanges?.overall?.min ?? '0',
    salaryRanges?.overall?.max ?? '5000',
  ]);

  const [definedSalaryRanges, setDefinedSalaryRanges] = useState<{ min: string; max: string }>({
    min: salaryRanges?.overall?.min || '0',
    max: salaryRanges?.overall?.max || '5000',
  });

  const handleSalaryChange = (newSalary: number[]) => {
    setFilter({ min_salary: newSalary[0].toString(), max_salary: newSalary[1].toString() });
    setSalaryRange(newSalary.map(val => val.toString()));
  };

  useEffect(() => {
    const setIfChanged = (nextMin?: string, nextMax?: string) => {
      const nextRange = [nextMin ?? '0', nextMax ?? '5000'];
      const currentMin = definedSalaryRanges.min;
      const currentMax = definedSalaryRanges.max;
      const shouldUpdateRange = currentMin !== nextRange[0] || currentMax !== nextRange[1];

      if (shouldUpdateRange) {
        setDefinedSalaryRanges({ min: nextRange[0], max: nextRange[1] });
        setSalaryRange(nextRange);
      }

      const filterMin = filter.min_salary ?? undefined;
      const filterMax = filter.max_salary ?? undefined;
      if (filterMin !== nextMin || filterMax !== nextMax) {
        setFilter({ min_salary: nextMin, max_salary: nextMax });
      }
    };

    if (
      !filter.salary_types?.length ||
      (filter.salary_types?.includes('1') && filter.salary_types?.includes('2'))
    ) {
      setIfChanged(salaryRanges?.overall?.min, salaryRanges?.overall?.max);
      return;
    }
    if (filter.salary_types?.includes('1')) {
      setIfChanged(salaryRanges?.['1']?.min, salaryRanges?.['1']?.max);
      return;
    }
    if (filter.salary_types?.includes('2')) {
      setIfChanged(salaryRanges?.['2']?.min, salaryRanges?.['2']?.max);
      return;
    }
  }, [
    filter.salary_types,
    filter.min_salary,
    filter.max_salary,
    salaryRanges,
    setFilter,
    definedSalaryRanges.min,
    definedSalaryRanges.max,
    salaryRange,
  ]);

  const isLoading =
    isLoadingCategories || isLoadingEmploymentTypes || isLoadingLanguages || isLoadingCountries;

  const selectedCategoriesSet = useMemo(
    () => new Set(filter?.categories ?? []),
    [filter?.categories],
  );
  const sortedCategories = useMemo(
    () =>
      [...categories].sort((a, b) => {
        const aSelected =
          selectedCategoriesSet.has(a.id) ||
          a.children.some(child => selectedCategoriesSet.has(child.id));
        const bSelected =
          selectedCategoriesSet.has(b.id) ||
          b.children.some(child => selectedCategoriesSet.has(child.id));
        return Number(bSelected) - Number(aSelected);
      }),
    [categories, selectedCategoriesSet],
  );

  useEffect(() => {
    const term = debouncedCategoriesSearch.trim().toLowerCase();
    if (!term) {
      return;
    }

    const matchedParents = sortedCategories
      .filter(
        parent =>
          parent.children.length > 0 &&
          (parent.label.toLowerCase().includes(term) ||
            parent.children.some(child => child.label.toLowerCase().includes(term))),
      )
      .map(parent => `category-${parent.id}`);

    setOpenedCategoryAccordions(prev => {
      if (
        prev.length === matchedParents.length &&
        prev.every((value, idx) => value === matchedParents[idx])
      ) {
        return prev;
      }
      return matchedParents;
    });
  }, [debouncedCategoriesSearch, sortedCategories]);
  const selectedCountriesSet = new Set(filter?.countries ?? []);
  const sortedCountries = countries.length > 0 ? [...countries] : [];

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
      }}
      className={cn(
        'flex flex-col gap-6 transition-opacity duration-500',
        isLoading ? 'animate-pulse opacity-60' : 'duration-500 animate-in fade-in-50',
        'ea-job-portal-offers-filter',
      )}
    >
      {/* <div className={cn('flex flex-col gap-6 md:flex-row', 'ea-job-portal-offers__input-row')}>
        <div className={cn('flex-1', 'ea-job-portal-offers__search-input')}>
          <Input
            placeholder={t('text869')}
            value={internalState.search}
            onChange={e => setInternalState({ ...internalState, search: e.target.value })}
            variant={JOBPORTAL_CONFIG.jobportalOffersFilter.searchInput.inputVariant}
            after={React.cloneElement(
              JOBPORTAL_CONFIG.jobportalOffersFilter.searchInput.rightIcon,
              {
                className: cn(
                  'mr-3 h-4 w-4 text-muted-foreground',
                  'ea-job-portal-offers__search-icon',
                ),
              },
            )}
          />
        </div>
        <div className="flex flex-row justify-end">
          <Button
            variant={JOBPORTAL_CONFIG.jobportalOffersFilter.searchButton.variant}
            onClick={() => setFilter({ search: '' })}
          >
            {t('text871')}
          </Button>
        </div>
      </div> */}

      {showCategoriesSkeleton ? (
        <div className="space-y-3">
          <div className="h-6 w-32 rounded bg-gray-200" />
          <div className="space-y-2">
            <div className="h-10 w-full rounded bg-gray-200" />
            <div className="h-8 w-3/4 rounded bg-gray-200" />
            <div className="h-8 w-2/3 rounded bg-gray-200" />
          </div>
        </div>
      ) : (
        <div className={cn('flex w-full flex-col gap-3')}>
          <div className="text-start text-lg font-semibold text-muted-foreground">
            {t('text3441')}
          </div>
          <Input
            placeholder={t('text871')}
            value={categoriesSearch}
            onChange={e => setCategoriesSearch(e.target.value)}
            className={{
              wrapper: 'rounded-[8px] border border-border',
              main: 'rounded-[8px] px-4',
            }}
          />
          <div
            className="flex max-h-80 flex-col gap-2 overflow-y-auto overflow-x-hidden"
            onScroll={e => {
              const el = e.currentTarget;
              if (
                categoriesQuery.hasNextPage &&
                !categoriesQuery.isFetchingNextPage &&
                el.scrollTop + el.clientHeight >= el.scrollHeight - 24
              ) {
                categoriesQuery.fetchNextPage();
              }
            }}
          >
            <Accordion
              type="multiple"
              className="w-full space-y-1"
              value={openedCategoryAccordions}
              onValueChange={setOpenedCategoryAccordions}
            >
              {sortedCategories.map(parent => {
                const parentChecked = selectedCategoriesSet.has(parent.id);
                const hasChildren = parent.children.length > 0;
                const parentAccordionValue = `category-${parent.id}`;
                const isParentAccordionOpen =
                  openedCategoryAccordions.includes(parentAccordionValue);
                return (
                  <AccordionItem
                    key={parent.id}
                    value={parentAccordionValue}
                    className="border-none"
                  >
                    <div className="flex items-center justify-between gap-2 py-2">
                      <label className="flex cursor-pointer items-start gap-3 text-sm">
                        <Checkbox
                          className="mt-0.5 h-5 w-5"
                          checked={parentChecked}
                          onCheckedChange={() => {
                            const set = new Set(filter?.categories ?? []);
                            if (set.has(parent.id)) set.delete(parent.id);
                            else set.add(parent.id);
                            setFilter({ categories: Array.from(set) });
                          }}
                        />
                        <span
                          className={cn(
                            'whitespace-normal break-words pr-2 leading-5 text-black',
                            (parentChecked || isParentAccordionOpen) && 'font-bold',
                          )}
                        >
                          {parent.label}
                        </span>
                      </label>
                      {hasChildren && <AccordionTrigger className="py-0" />}
                    </div>
                    {hasChildren && (
                      <AccordionContent className="pb-2 pl-2">
                        <div className="flex flex-col gap-2">
                          {parent.children.map(child => {
                            const childChecked = selectedCategoriesSet.has(child.id);
                            return (
                              <label
                                key={child.id}
                                className="flex cursor-pointer items-start gap-3 text-sm"
                              >
                                <Checkbox
                                  className="mt-0.5 h-5 w-5"
                                  checked={childChecked}
                                  onCheckedChange={() => {
                                    const set = new Set(filter?.categories ?? []);
                                    if (set.has(child.id)) set.delete(child.id);
                                    else set.add(child.id);
                                    setFilter({ categories: Array.from(set) });
                                  }}
                                />
                                <span
                                  className={cn(
                                    'whitespace-normal break-words pr-2 leading-5 text-black',
                                    childChecked && 'font-semibold text-foreground',
                                  )}
                                >
                                  {child.label}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    )}
                  </AccordionItem>
                );
              })}
            </Accordion>
            {!categoriesQuery.isFetching && sortedCategories.length === 0 && (
              <div className="text-xs text-muted-foreground">{t('text477')}</div>
            )}
            {categoriesQuery.isFetchingNextPage && (
              <div className="text-xs text-muted-foreground">{t('text338')}</div>
            )}
            {categoriesQuery.isFetching && !categoriesQuery.isFetchingNextPage && (
              <div className="text-xs text-muted-foreground">{t('text338')}</div>
            )}
          </div>
        </div>
      )}

      {isLoadingCountries ? (
        <div className="space-y-3">
          <div className="h-6 w-32 rounded bg-gray-200" />
          <div className="space-y-2">
            <div className="h-8 w-3/4 rounded bg-gray-200" />
            <div className="h-8 w-2/3 rounded bg-gray-200" />
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-3">
          <div className="text-start text-lg font-semibold text-[#63626F]">{t('text41')}</div>
          <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
            {sortedCountries.length === 0 ? (
              <div className="text-xs text-muted-foreground">{t('text554')}</div>
            ) : (
              sortedCountries.map(country => {
                const value = String(country.id);
                const checked = selectedCountriesSet.has(value);
                return (
                  <label
                    key={country.id}
                    className="flex cursor-pointer items-center gap-3 text-sm"
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => {
                        const set = new Set(filter?.countries ?? []);
                        if (set.has(value)) set.delete(value);
                        else set.add(value);
                        setFilter({ countries: Array.from(set) });
                      }}
                    />
                    <span className={cn('truncate', checked && 'font-semibold text-foreground')}>
                      {country.name}
                    </span>
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
      <Separator className="bg-[#D8D8DB]" />

      {isLoading ? (
        <div className="space-y-3">
          <div className="h-6 w-32 rounded bg-gray-200" />
          <div className="h-8 w-full rounded bg-gray-200" />
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="w-full text-start text-lg font-semibold text-[#63626F]">
            {t('text893')}
          </div>
          <RangeFilter
            value={salaryRange.map(val => parseInt(val))}
            min={definedSalaryRanges.min ? parseInt(definedSalaryRanges.min) : 0}
            max={definedSalaryRanges.max ? parseInt(definedSalaryRanges.max) : 5000}
            onValueChange={handleSalaryChange}
            formatValue={(salary: any) => salary.toLocaleString()}
            currency={currency}
          />
        </div>
      )}
      <Separator className="bg-[#D8D8DB]" />

      {isLoadingEmploymentTypes ? (
        <div className="space-y-3">
          <div className="h-6 w-32 rounded bg-gray-200" />
          <div className="space-y-2">
            <div className="h-8 w-3/4 rounded bg-gray-200" />
            <div className="h-8 w-2/3 rounded bg-gray-200" />
          </div>
        </div>
      ) : (
        employmentTypes && (
          <CheckboxesFilter
            title={t('text894')}
            options={employmentTypes}
            selectedOptions={filter?.employment_types ?? []}
            onChange={(newEmploymentTypes: any) => {
              setFilter({ employment_types: newEmploymentTypes });
            }}
          />
        )
      )}
      <Separator className="bg-[#D8D8DB]" />

      {isLoadingLanguages ? (
        <div className="space-y-3">
          <div className="h-6 w-32 rounded bg-gray-200" />
          <div className="space-y-2">
            <div className="h-8 w-3/4 rounded bg-gray-200" />
            <div className="h-8 w-2/3 rounded bg-gray-200" />
          </div>
        </div>
      ) : (
        languages && (
          <CheckboxesFilter
            title={t('text1612')}
            options={languages}
            selectedOptions={filter?.language ?? []}
            onChange={(newLanguages: any) => {
              setFilter({ language: newLanguages });
            }}
          />
        )
      )}
      {props.showClearButton !== false && (
        <>
          <Separator className="bg-[#D8D8DB]" />
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              clearFilters();
            }}
          >
            {t('text2476')}
          </Button>
        </>
      )}
    </form>
  );
}
